/**
 * @file PlayerResultItemView.tsx
 * @description Pure presentation component for displaying a player search result
 */
import { memo } from "react";

import ManualPlayerBadge from "@/components/molecules/ManualPlayerBadge";
import { Stack } from "@/components/atoms/Stack";
import { Pressable } from "@/components/atoms/Pressable";
import { Text } from "@/components/atoms/Text";

/**
 * Props for PlayerResultItemView component
 */
export interface PlayerResultItemViewProps {
  readonly playerName: string;
  readonly positionAndTeam: string;
  readonly isManual: boolean;
  readonly onPress: () => void;
  readonly isDark: boolean;
}

/**
 * Pure presentation component for player result item
 * @param root0 - Component props
 * @param root0.playerName - The player's display name
 * @param root0.positionAndTeam - The player's position and team info
 * @param root0.isManual - Whether the player was manually created by an organization user
 * @param root0.onPress - Callback when item is pressed
 * @param root0.isDark - Whether dark theme is active
 */
const PlayerResultItemView = ({
  playerName,
  positionAndTeam,
  isManual,
  onPress,
  isDark,
}: PlayerResultItemViewProps) => (
  <Pressable
    onPress={onPress}
    className={`border-b px-4 py-3 ${isDark ? "border-outline-emphasis active:bg-surface-subtle" : "border-surface-subtle active:bg-surface-raised"}`}
  >
    <Stack>
      {/* Renders the backend-canonical `player.name` (SE-5469) — already the
          full, particle-safe display name; never re-assemble from parts. */}
      <Stack direction="horizontal" className="items-center gap-2">
        <Text
          variant="title-sm"
          className={isDark ? "text-white" : "text-content-primary"}
        >
          {playerName}
        </Text>
        <ManualPlayerBadge show={isManual} compact />
      </Stack>
      <Text
        variant="body"
        className={`${isDark ? "text-content-subtle" : "text-content-neutral-strong"}`}
      >
        {positionAndTeam}
      </Text>
    </Stack>
  </Pressable>
);

PlayerResultItemView.displayName = "PlayerResultItemView";

export default memo(PlayerResultItemView);
